/**
 * The evenings you have played.
 *
 * A party used to stop existing the morning after, which is why clips could
 * only be kept against a crew game night - there was nothing else left to
 * attach them to. A party now survives as a session for a fortnight: who was
 * in it, what you were playing, and whatever anybody kept from it.
 *
 * Which also answers the smaller question people actually ask - "who were
 * those two we played with on Tuesday" - without anybody having to add
 * anybody as a friend to find out.
 */

import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBackdropClose } from '@/lib/backdrop';
import { useLayer } from '@/lib/layers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, serverUrl } from '@/lib/api';
import type { Party, PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { ago } from '@/lib/notifications';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { Lightbox } from './Lightbox';
import { absolute } from './user';
import { Button, useToast } from './ui';

export type Highlight = {
  id: string;
  fileId: string;
  url: string;
  type: string;
  name: string;
  by: string;
  at: number;
  note: string;
  user?: PublicUser | null;
};

type Session = {
  id: string;
  title: string;
  gameId: string;
  closed: boolean;
  when: number;
  players: PublicUser[];
  highlights: Highlight[];
};

/** The card on Home: your last few evenings, with what was kept from them. */
export function RecentSessions({ limit = 3 }: { limit?: number }) {
  const me = useSession((s) => s.me);
  const q = useQuery({
    queryKey: ['sessions', 'recent'],
    queryFn: () => api.get<{ sessions: Session[] }>('/api/sessions/recent'),
    enabled: !!me,
    staleTime: 60_000
  });

  const list = (q.data?.sessions ?? []).slice(0, limit);
  if (!list.length) return null;

  return (
    <section
      aria-labelledby="sessions-title"
      className="pugo-panel rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]"
    >
      <div className="mb-3 flex items-center gap-2.5">
        <Icon name="history" size={19} className="text-ink" />
        <h2 id="sessions-title" className="m-0 flex-1 text-[17px] font-bold text-ink">Evenings you have played</h2>
      </div>

      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {list.map((s) => (
          <li key={s.id} className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
            <div className="flex items-center gap-2.5">
              <GameMark id={s.gameId} size={28} plain />
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-[14px] font-bold text-ink">{s.title}</p>
                <p className="m-0 truncate text-[12px] text-mute">
                  {ago(s.when)}{s.closed ? '' : ' · still going'}
                  {s.players.length ? ' · with ' + s.players.slice(0, 2).map((p) => p.displayName).join(', ') : ''}
                  {s.players.length > 2 ? ' and ' + (s.players.length - 2) + ' more' : ''}
                </p>
              </div>
              <span className="flex flex-none items-center">
                {s.players.slice(0, 4).map((u, i) => (
                  <span key={u.id} className="rounded-full ring-2 ring-[var(--bg-2)]" style={{ marginLeft: i ? -8 : 0 }}>
                    <Avatar user={u} size={22} />
                  </span>
                ))}
              </span>
              {!s.closed ? (
                <Button size="sm" kind="outline" onClick={() => go('party/' + s.id)}>Open</Button>
              ) : null}
            </div>

            <SessionClips partyId={s.id} highlights={s.highlights} />
          </li>
        ))}
      </ul>

      <p className="mt-3 mb-0 text-[11.5px] text-ghost">Kept for a fortnight, then it goes.</p>
    </section>
  );
}

/**
 * Clips from one session.
 *
 * Anybody who was in the party can add and remove their own; the host and
 * officers can remove any of them. Shared with the room, not with the world.
 */
export function SessionClips({ partyId, highlights, compact }: {
  partyId: string;
  highlights: Highlight[];
  /** Inside a live party, where space is tighter. */
  compact?: boolean;
}) {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const file = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Highlight[]>(highlights ?? []);
  const [busy, setBusy] = useState(false);
  const [look, setLook] = useState<Highlight | null>(null);

  async function add(f: File) {
    setBusy(true);
    try {
      const up = await fetch(serverUrl() + '/api/files', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''),
          'Content-Type': f.type || 'application/octet-stream',
          'X-File-Type': f.type || 'application/octet-stream',
          'X-File-Name': encodeURIComponent(f.name),
          'X-File-Kind': 'attachment'
        },
        body: f
      });
      if (!up.ok) throw new Error('That file would not upload.');
      const saved = await up.json();
      const out = await api.post<{ highlights: Highlight[] }>('/api/parties/' + partyId + '/highlights', {
        fileId: saved.id ?? saved.file?.id ?? saved.attachment?.id
      });
      setRows(out.highlights);
      void queryClient.invalidateQueries({ queryKey: ['sessions', 'recent'] });
      void queryClient.invalidateQueries({ queryKey: ['party', partyId] });
      toast('Kept with this session', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
      if (file.current) file.current.value = '';
    }
  }

  async function remove(id: string) {
    try {
      const out = await api.del<{ highlights: Highlight[] }>('/api/parties/' + partyId + '/highlights/' + id);
      setRows(out.highlights);
      void queryClient.invalidateQueries({ queryKey: ['sessions', 'recent'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const pictures = rows.filter((h) => h.type.startsWith('image/'));

  /*
   * A clip people can drop on.
   *
   * The button is still there - it is the discoverable route - but the
   * thing everybody tries first with a screenshot is to drag it onto the
   * place it belongs.
   */
  const [over, setOver] = useState(false);

  return (
    <div
      className={cn(
        compact ? 'mt-2' : 'mt-2.5',
        'rounded-[calc(12px*var(--radius-mul,1))] transition-[background-color,box-shadow] duration-[var(--t-hover)]',
        over && 'bg-accent-soft shadow-[inset_0_0_0_2px_var(--accent)]'
      )}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const dropped = e.dataTransfer?.files?.[0];
        if (dropped) void add(dropped);
      }}
    >
      {rows.length ? (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {rows.map((h) => (
            <li key={h.id} className="relative">
              <button
                type="button"
                onClick={() => setLook(h)}
                title={(h.note || h.name) + (h.by ? '' : '')}
                className={cn(
                  'group/clip relative block overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] bg-surface-3 outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                  compact ? 'h-[52px] w-[78px]' : 'h-[64px] w-[96px]'
                )}
              >
                {h.type.startsWith('image/') ? (
                  <img src={absolute(h.url)} alt={h.note || h.name} className="h-full w-full object-cover" />
                ) : (
                  <>
                    {/*
                      The first frame, as the thumbnail.

                      preload="metadata" fetches the header rather than the
                      clip, and #t=0.1 asks for a frame just past the start -
                      some encodes open on a black one.
                    */}
                    <video
                      src={absolute(h.url) + '#t=0.1'}
                      preload="metadata"
                      muted
                      playsInline
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute inset-0 grid place-items-center bg-[rgba(0,0,0,.28)] text-white/90">
                      <Icon name="play" size={16} />
                    </span>
                  </>
                )}
              </button>
              {h.by === me?.id ? (
                <button
                  type="button"
                  aria-label="Remove this clip"
                  onClick={() => void remove(h.id)}
                  className="absolute top-1 right-1 grid h-5 w-5 place-items-center rounded-full bg-[rgba(0,0,0,.6)] text-white/80 transition-colors hover:text-white"
                >
                  <Icon name="close" size={10} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        disabled={busy}
        onClick={() => file.current?.click()}
        className="mt-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-mute outline-none transition-colors hover:text-accent"
      >
        <Icon name="image" size={13} />
        {busy ? 'Adding…' : rows.length ? 'Add another' : 'Keep a clip from this'}
      </button>
      <input
        ref={file}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void add(f); }}
      />

      {/* A clip nobody can watch is a file, not a clip. The lightbox is
          pictures only, so a video opens in its own small player. */}
      {look && !look.type.startsWith('image/') ? (
        <ClipPlayer clip={look} onClose={() => setLook(null)} />
      ) : null}

      {look && look.type.startsWith('image/') ? (
        <Lightbox
          list={pictures.map((h) => ({ id: h.fileId, url: h.url, name: h.note || h.name, type: h.type, size: 0, image: true }))}
          index={Math.max(0, pictures.findIndex((h) => h.id === look.id))}
          onClose={() => setLook(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * A clip, playing.
 *
 * The lightbox is built for pictures - it pans, it zooms, it steps through a
 * list - and none of that means anything for a video, so a clip got a grey
 * box with a film icon in it and no way to watch the thing somebody kept.
 *
 * This is the small version: the clip, the browser's own controls, and a way
 * to save it. Whoever kept it and when, because a fortnight later that is
 * the part nobody remembers.
 */
function ClipPlayer({ clip, onClose }: { clip: Highlight; onClose: () => void }) {
  const backdrop = useBackdropClose(onClose);
  const shell = useRef<HTMLDivElement>(null);
  useLayer(true, { node: () => shell.current, close: onClose });

  return createPortal(
    <div
      ref={shell}
      data-keeps-card
      className="soft-in fixed inset-0 z-[400] grid place-items-center bg-[rgba(3,5,8,.72)] p-4"
      {...backdrop}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={clip.note || clip.name || 'Clip'}
        className="flex w-full max-w-[860px] flex-col overflow-hidden rounded-[calc(18px*var(--radius-mul,1))] border border-line bg-surface-0 shadow-[var(--shadow-3)]"
      >
        <div className="flex items-center gap-2 px-3 py-2.5">
          <Icon name="video" size={15} className="flex-none text-accent" />
          <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-ink">{clip.note || clip.name || 'Clip'}</span>
          <a
            href={absolute(clip.url)}
            download={clip.name || 'clip'}
            className="rounded-[8px] px-2 py-1 text-[12px] font-semibold text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink"
          >
            Save
          </a>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-7 w-7 flex-none place-items-center rounded-[8px] text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink"
          >
            <Icon name="close" size={14} />
          </button>
        </div>
        <video
          src={absolute(clip.url)}
          controls
          autoPlay
          playsInline
          className="max-h-[70vh] w-full bg-black"
        />
      </div>
    </div>,
    document.body
  );
}

/** The same shelf, inside the party it belongs to. */
export function PartyClips({ party }: { party: Party }) {
  if (!party.isMember) return null;
  return (
    <section className="pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 p-3 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="flex items-center gap-2">
        <Icon name="image" size={15} className="flex-none text-accent" />
        <h3 className="m-0 flex-1 text-[13.5px] font-bold text-ink">Clips from tonight</h3>
        {(party.highlights ?? []).length ? (
          <span className="text-[12px] font-semibold text-mute tabular-nums">{party.highlights!.length}</span>
        ) : null}
      </div>
      <SessionClips partyId={party.id} highlights={party.highlights ?? []} compact />
      <p className="m-0 mt-1.5 text-[11px] text-ghost">Kept with this session for a fortnight, for the people who were in it.</p>
    </section>
  );
}
